/**
 * Auth request and response schemas.
 *
 * The password rules live here so the sign-up form and the server enforce exactly
 * the same thing, and so the copy shown next to the field is generated from the
 * rule rather than written separately and left to drift.
 */

import { z } from 'zod';

import { emailSchema } from './common.schema.js';

/**
 * Minimum password length.
 *
 * Length is the rule, not a character-class puzzle. Composition rules push people toward
 * predictable substitutions and shorter passwords, which is why current guidance favours a
 * long minimum instead.
 *
 * Set to 10 rather than the 12 first chosen here, because TR picked a 10 character
 * credential for the owner account and seeding a password the application itself would
 * reject would mean it could never be re-entered on the change-password screen. The
 * tradeoff is recorded rather than hidden: 10 characters with mixed case, a digit and a
 * symbol is defensible for this product, and 12 would be better.
 */
export const PASSWORD_MIN_LENGTH = 10;

/**
 * Maximum password length.
 *
 * bcrypt truncates at 72 bytes, so anything past that silently stops adding
 * security. Rejecting it is honest; accepting it quietly is not.
 */
export const PASSWORD_MAX_LENGTH = 72;

/** Human-readable rule, rendered next to the field so copy and rule cannot drift. */
export const PASSWORD_RULE_TEXT =
  `At least ${PASSWORD_MIN_LENGTH} characters. Length matters more than symbols, ` +
  'so a short phrase you will remember beats a short password you will not.';

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(PASSWORD_MAX_LENGTH, `Use at most ${PASSWORD_MAX_LENGTH} characters`);

/** Shown in the account list of active sessions, so the user can revoke one. */
export const deviceLabelSchema = z.string().trim().min(1).max(60);

/**
 * The browser's own IANA zone name (`Intl.DateTimeFormat().resolvedOptions().timeZone`),
 * sent once at sign-in so a session's location can read something better than a raw
 * IP - "Asia/Kolkata" rather than nothing, with no lookup service and no external
 * request involved at all.
 */
export const timezoneSchema = z.string().trim().min(1).max(60).optional();

export const registerRequestSchema = z.strictObject({
  email: emailSchema,
  password: passwordSchema,
  displayName: z.string().trim().min(1, 'Enter your name').max(80),
  deviceLabel: deviceLabelSchema.optional(),
  timezone: timezoneSchema,
});

export const loginRequestSchema = z.strictObject({
  email: emailSchema,
  password: z.string().min(1, 'Enter your password').max(PASSWORD_MAX_LENGTH),
  deviceLabel: deviceLabelSchema.optional(),
  timezone: timezoneSchema,
});

/** Session nicknames are cosmetic only - never the authority for "this device". */
export const sessionNicknameRequestSchema = z.strictObject({
  nickname: z.string().trim().max(60).nullable(),
});

export const passwordResetRequestSchema = z.strictObject({
  email: emailSchema,
});

export const passwordResetConfirmSchema = z.strictObject({
  token: z.string().min(20).max(200),
  password: passwordSchema,
});

export const changePasswordRequestSchema = z.strictObject({
  currentPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH),
  newPassword: passwordSchema,
  timezone: timezoneSchema,
});

export const revokeSessionParamsSchema = z.strictObject({
  sessionId: z.string().regex(/^[0-9a-f]{24}$/, 'Expected a session id'),
});

/**
 * What the server returns about the signed-in user.
 *
 * Declared as a schema so a contract test can assert the response never grows a
 * field by accident, in particular never a credential.
 */
export const sessionUserSchema = z.strictObject({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  createdAt: z.string(),
});
